import type { DocPage } from '../types'

export const introduction: DocPage = {
  id: 'introduction',
  title: 'Introduction',
  description:
    'SEPM X-Translator is a desktop application for transferring geospatial and CAD data between formats with structured mapping control.',
  category: 'Getting started',
  sections: [
    { id: 'what-is', title: 'What is X-Translator?' },
    { id: 'who-for', title: 'Who this manual is for' },
    { id: 'how-it-works', title: 'How the application works' },
    { id: 'requirements', title: 'System requirements' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'what-is',
      text: 'What is X-Translator?',
    },
    {
      type: 'paragraph',
      text: 'SEPM X-Translator is a desktop product for reading data from a source format, mapping collections and attributes, and writing results to a target format. It supports databases (such as PostGIS and Smallworld), file formats (GeoPackage, Shapefile, GeoJSON, KML, GeoParquet, text/CSV/Excel), AutoCAD drawings, and SEPM’s native X-Format.',
    },
    {
      type: 'paragraph',
      text: 'Transfers can be mapped (you define how source structures connect to target structures) or scratch (X-Translator creates the target schema from the source). Optional tools include Auto Map, fixed and value mappings, Connect-ID updates, spatial filters, style import/export, transfer profiles, and transfer history.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'who-for',
      text: 'Who this manual is for',
    },
    {
      type: 'paragraph',
      text: 'This manual is written for people who use X-Translator day to day: GIS specialists, CAD operators, data migration teams, and organization members who need to understand formats, mapping, and account access. It focuses on the UI and workflows, not on installing or developing the backends.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'how-it-works',
      text: 'How the application works',
    },
    {
      type: 'list',
      items: [
        'The desktop shell (Electron) shows the X-Translator workspace.',
        'A local Python backend handles format I/O, mapping, and transfers.',
        'A local CAD service handles AutoCAD .dwg / .dxf read and write.',
        'Sign-in and entitlements come from your SEPM account (USA / accounts.sepm.ch).',
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Local backends',
      text: 'When you launch X-Translator, a splash screen starts the Python and CAD backends. Wait until they are ready before signing in or opening models.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'requirements',
      text: 'System requirements',
    },
    {
      type: 'list',
      items: [
        'A supported Windows desktop environment (primary deployment target).',
        'Network access to SEPM account services for sign-in and entitlement refresh.',
        'A SEPM organization membership with a LIVE X-Translator subscription seat for the formats and features you need.',
        'Enough disk space for source/target files and transfer logs.',
      ],
    },
  ],
}
